import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@vaiu/ui/card";
import { Button } from "@vaiu/ui/button";
import { Badge } from "@vaiu/ui/badge";
import { Check, Palette, Eye } from 'lucide-react';
import { useBrandThemeContext } from '@/context/BrandThemeContext';
import ThemeModeSelector from '@/components/ThemeModeSelector';

const BrandThemeSelector = () => {
  const { currentBrand, theme, switchBrand, getAvailableBrands, clearUserPreferences, getThemeSource } = useBrandThemeContext();

  const getBrandDisplayName = (brandKey: string) => {
    switch (brandKey) {
      case 'clean':
        return 'Clean Slate';
      case 'rbko':
        return 'Golden Hour';
      case 'bnp':
        return 'Forest Green';
      case 'payformyfood':
        return 'Crimson Fire';
      case 'ing':
        return 'ING Orange';
      case 'vaiu':
        return 'Sunset Orange';
      default:
        return brandKey.charAt(0).toUpperCase() + brandKey.slice(1);
    }
  };

  const getBrandDescription = (brandKey: string) => {
    switch (brandKey) {
      case 'clean':
        return 'Minimal grey palette - clean and professional';
      case 'rbko':
        return 'Radiant yellow and gold accents with dark contrast';
      case 'bnp':
        return 'Deep forest green with ocean blue highlights';
      case 'payformyfood':
        return 'Bold crimson red with warm orange touches';
      case 'ing':
        return 'ING lion orange with indigo accents';
      case 'vaiu':
        return 'Vibrant sunset orange with sleek black accents';
      default:
        return 'Custom brand color palette';
    }
  };

  const getBrandPreviewColors = (brandKey: string) => {
    switch (brandKey) {
      case 'clean':
      case 'vaiu':
        return { primary: '#1F2937', secondary: '#F3F4F6', accent: '#F3F4F6' };
      case 'rbko':
        return { primary: '#FFEB3B', secondary: '#000000', accent: '#FFC107' };
      case 'bnp':
        return { primary: '#00A651', secondary: '#003D82', accent: '#FFD700' };
      case 'ing':
        return { primary: '#FF6200', secondary: '#525199', accent: '#FF6200' };
      case 'payformyfood':
        return { primary: '#DC2626', secondary: '#991B1B', accent: '#F59E0B' };
      default:
        return { primary: '#FF6600', secondary: '#000000', accent: '#FF8C00' };
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Palette className="w-5 h-5" />
          <span>Styling</span>
        </CardTitle>
        <CardDescription>
          Choose the visual style and color palette for this application
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {getAvailableBrands().map((brandKey) => {
            const previewColors = getBrandPreviewColors(brandKey);
            return (
            <div key={brandKey} className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium flex items-center gap-2">
                    {getBrandDisplayName(brandKey)}
                    {currentBrand === brandKey && (
                      <Badge variant="secondary" className="bg-green-100 text-green-800">
                        <Check className="w-3 h-3 mr-1" />
                        Active
                      </Badge>
                    )}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {getBrandDescription(brandKey)}
                  </p>
                </div>
                <Button
                  variant={currentBrand === brandKey ? "default" : "outline"}
                  size="sm"
                  onClick={() => switchBrand(brandKey)}
                >
                  {currentBrand === brandKey ? 'Selected' : 'Select'}
                </Button>
              </div>
              
              {/* Preview */}
              <div className="border rounded-lg p-3 bg-gray-50">
                <p className="text-xs text-gray-600 mb-2">Color Preview:</p>
                <div className="flex space-x-2">
                  <div 
                    className="w-6 h-6 rounded-full border"
                    style={{ backgroundColor: previewColors.primary }}
                    title="Primary Color"
                  />
                  <div 
                    className="w-6 h-6 rounded-full border"
                    style={{ backgroundColor: previewColors.secondary }}
                    title="Secondary Color"
                  />
                  <div 
                    className="w-6 h-6 rounded-full border"
                    style={{ backgroundColor: previewColors.accent }}
                    title="Accent Color"
                  />
                </div>
              </div>
            </div>
          );
          })}
        </div>

        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">Current Style Details</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-blue-700">Style:</span>
              <span className="font-medium text-blue-900">{theme.companyName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-blue-700">Primary Color:</span>
              <div className="flex items-center space-x-2">
                <div 
                  className="w-4 h-4 rounded border"
                  style={{ backgroundColor: theme.primaryColor }}
                />
                <span className="font-mono text-xs text-blue-900">{theme.primaryColor}</span>
              </div>
            </div>
            <div className="flex justify-between">
              <span className="text-blue-700">Secondary Color:</span>
              <div className="flex items-center space-x-2">
                <div 
                  className="w-4 h-4 rounded border"
                  style={{ backgroundColor: theme.secondaryColor }}
                />
                <span className="font-mono text-xs text-blue-900">{theme.secondaryColor}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Style Source Info & Reset */}
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-sm">Style Source</h4>
              <p className="text-xs text-muted-foreground">
                Current style is from: <span className="font-medium">{getThemeSource()}</span>
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={clearUserPreferences}
              className="text-xs"
            >
              Reset to Default
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const BrandThemeSelectorWithModeSelector = () => {
  return (
    <div className="space-y-6">
      <BrandThemeSelector />
      <ThemeModeSelector />
    </div>
  );
};

export default BrandThemeSelector; 